<template>
    <v-dialog
        max-width="900px"
        :model-value="dialog"
        @update:model-value="$emit('update:dialog', $event)"
        @click:outside="cancel"
        @keyup.esc="cancel"
    >
        <v-card v-if="wizardStep == 'upload'">
            <v-card-title class="text-h5 bg-grey-lighten-2" primary-title style="display: relative">
                Upload plate data
            </v-card-title>
            <v-card-text>
                <v-form class="pt-8">
                    <v-row class="mx-2">
                        <v-col cols="3" md="5">
                            <v-date-input v-model="collectionDate" label="Collection Date" />
                            <p style="padding-top: 16px; font-size: small">Measurement Type</p>
                            <v-radio-group v-model="measurementType">
                                <v-radio label="Fluorescence" value="fluorescence"></v-radio>
                                <v-radio label="Luminescence" value="luminescence"></v-radio>
                                <v-radio label="OD" value="od"></v-radio>
                            </v-radio-group>
                        </v-col>
                        <v-col cols="9" md="7">
                            <DragDropFileUploader
                                :acceptedFileTypes="[
                                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                                ]"
                                @filesUploaded="uploadFiles($event)"
                                style="min-height: 240px; width: 100%"
                            />
                        </v-col>
                    </v-row>
                </v-form>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn
                    color="primary"
                    @click="submitFiles"
                    :disabled="originalFiles.length == 0 || !collectionDate || !measurementType"
                    >Next
                </v-btn>
                <v-btn color="primary" @click="cancel">Cancel</v-btn>
            </v-card-actions>
        </v-card>
        <v-card v-if="wizardStep == 'mapPlates'">
            <v-card-title class="text-h5 bg-grey-lighten-2" primary-title style="display: relative">
                Map files to plates
            </v-card-title>
            <v-card-text>
                <MapFilesToContainers
                    :files="preprocessedFiles"
                    :groupsToContainersMap="groupsToPlatesMap"
                    containerType="plate"
                    @mappingChanged="mappingChanged($event)"
                />
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn @click="sendFilesToBackend" :disabled="Object.values(plateMap).length === 0"
                    >Submit</v-btn
                >
            </v-card-actions>
        </v-card>
        <v-card v-if="wizardStep == 'review'">
            <v-card-title class="text-h5 bg-grey-lighten-2" primary-title style="display: relative">
                Review
            </v-card-title>
            <v-card-text>
                <div style="padding-top: 8px">
                    <h3>
                        <v-icon v-if="submissionStatus === 'submitted'">$timer-sand</v-icon>
                        <v-icon v-else-if="submissionStatus === 'completed'" color="success"
                            >$check-circle</v-icon
                        >
                        <v-icon
                            v-else-if="
                                submissionStatus === 'error' || submissionStatus === 'incomplete'
                            "
                            color="error"
                            >$alert-circle</v-icon
                        >
                        {{ constructUploadHeadline() }}
                    </h3>
                    <v-list>
                        <v-list-item v-for="result in uploadResult" :key="result.file">
                            <v-list-item-title>
                                <v-icon v-if="result.status == 'Pending'">$timer-sand</v-icon>
                                <v-icon v-else-if="result.status == 'Completed'" color="success"
                                    >$check-circle</v-icon
                                >
                                <v-icon v-else-if="result.status == 'Error'" color="error"
                                    >$alert-circle</v-icon
                                >
                                {{ result.file }}
                            </v-list-item-title>
                        </v-list-item>
                    </v-list>
                </div>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn @click="cancel">Close</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { defineComponent } from "vue";

import axios from "../../lib/apiAxios";
import * as ExcelJS from "exceljs";
import DragDropFileUploader from "@/components/common/DragDropFileUploader.vue";
import MapFilesToContainers from "@/components/common/MapFilesToContainers.vue";
import { EventBus } from "../../lib/event-bus.js";
import { DateTime } from "luxon";

export default defineComponent({
    components: {
        DragDropFileUploader,
        MapFilesToContainers,
    },

    props: {
        dialog: Boolean,
        code: String, // Experiment code or request code
        codeType: String, // "experiment" or "request"
        plates: Array,
    },

    data() {
        return {
            wizardStep: "upload",
            originalFiles: [],
            preprocessedFiles: [],
            collectionDate: new Date(),
            measurementType: "",
            plateMap: {},
            draggedData: "",
            renamedFiles: [],
            disabledChips: [],
            uploadResult: [],
            submissionStatus: "",
            groupsToPlatesMap: {},
        };
    },

    methods: {
        cancel() {
            this.$emit("cancel");
        },
        async uploadFiles(files) {
            this.originalFiles = files;
            this.preprocessedFiles = [];
            await this.preprocessFiles();
        },
        async submitFiles() {
            this.buildGroupsToPlatesMap();
            this.wizardStep = "mapPlates";
        },
        async preprocessFiles() {
            for (let i = 0; i < this.originalFiles.length; i++) {
                const file = this.originalFiles[i];
                await this.splitIfMultiPlate(file);
            }
        },
        /**
         * This method peeks into each file to see if it uses the new multi-plate format. If so, it
         * splits the plates into individual files, omitting the first row. Otherwise, it uses the
         * file as is.
         */
        async splitIfMultiPlate(file) {
            const workbook = new ExcelJS.Workbook();
            workbook.xlsx.load(file).then((workbook) => {
                if (this.hasMultipleWorksheets(workbook)) {
                    // split the file into one file per worksheet
                    workbook.eachSheet((worksheet) => {
                        const newWorkbook = new ExcelJS.Workbook();
                        const newSheet = newWorkbook.addWorksheet("Sheet1");
                        const firstDataRow = this.hasNonEmptyFirstCell(worksheet) ? 2 : 1;
                        // if the first row contains data, we want to remove it in the new file
                        worksheet.eachRow((row, rowNumber) => {
                            if (rowNumber >= firstDataRow) {
                                // copy the values
                                newSheet.addRow(row.values);
                                // copy the formatting
                                row.eachCell((cell, colNumber) => {
                                    const newSheetRowNumber =
                                        firstDataRow === 2 ? rowNumber - 1 : rowNumber;
                                    newSheet.getCell(newSheetRowNumber, colNumber).style =
                                        cell.style;
                                });
                            }
                        });
                        const filename = file.name.replace(".xlsx", `_${worksheet.name}.xlsx`);
                        newWorkbook.xlsx.writeBuffer().then((buffer) => {
                            const newFile = new File([buffer], filename, { type: file.type });
                            this.preprocessedFiles.push(newFile);
                        });
                    });
                } else {
                    this.preprocessedFiles.push(file);
                }
            });
        },
        hasNonEmptyFirstCell(worksheet) {
            const firstRow = worksheet.getRow(1);
            const firstCell = firstRow.getCell(1);
            return firstCell.value !== null;
        },
        hasMultipleWorksheets(workbook) {
            return workbook.worksheets.length > 1;
        },
        async sendFilesToBackend() {
            Object.keys(this.plateMap).forEach((plate) => {
                if (this.plateMap[plate]) {
                    const file = this.preprocessedFiles.find(
                        (file) => file.name === this.plateMap[plate]
                    );
                    this.renamedFiles.push(this.renameFile(file, plate));
                }
            });
            const formData = new FormData();
            this.renamedFiles.forEach((file) => {
                formData.append("file", file);
            });
            const formJson = JSON.stringify({
                code: this.code,
                collectionDate: this.collectionDate,
            });
            const blob = new Blob([formJson], { type: "application/json" });
            formData.append("data", blob);

            try {
                const response = await axios.post(`/${this.getRoute()}/plate/data`, formData);
                Object.keys(response.data).forEach((file) => {
                    this.uploadResult.push({
                        file: file,
                        status: "Pending",
                        backgroundProcessId: response.data[file],
                    });
                });
                this.submissionStatus = "submitted";
            } catch (error) {
                this.uploadResult = [];
                this.submissionStatus = "error";
            }
            this.wizardStep = "review";
            await this.refreshUploadStatus();
            EventBus.$emit("uploadComplete");
        },
        getRoute() {
            return this.codeType === "experiment" ? "experiment" : "requests";
        },
        constructUploadHeadline() {
            const plateOrPlates = this.renamedFiles.length > 1 ? "plates" : "plate";
            const submissionHeadlines = {
                error: "An error occurred while submitting the files.",
                submitted: `${this.renamedFiles.length} ${plateOrPlates} submitted.`,
                incomplete: "Some files were not sucessfully uploaded.",
                completed: `${this.renamedFiles.length} ${plateOrPlates} successfully uploaded. Results will be available in Looker soon.`,
            };
            return submissionHeadlines[this.submissionStatus] || "No files have been submitted.";
        },
        async refreshUploadStatus() {
            let unfinishedUploads = this.uploadResult.filter(
                (upload) => upload.status === "Pending"
            );
            let retries = 0;
            while (unfinishedUploads.length > 0 && retries < 10) {
                unfinishedUploads.forEach(async (upload) => {
                    const response = await axios.get(
                        `/${this.getRoute()}/plate/status/${upload.backgroundProcessId}`
                    );
                    upload.status = response.data.status;
                });
                unfinishedUploads = this.uploadResult.filter(
                    (upload) => upload.status === "Pending"
                );
                // wait 1 second
                await new Promise((resolve) => setTimeout(resolve, 1000));
                retries++;
            }
            this.submissionStatus = unfinishedUploads.length === 0 ? "completed" : "incomplete";
        },
        renameFile(file, plateName) {
            const prefix = this.getFilenamePrefix(plateName);
            const datePart = DateTime.fromJSDate(this.collectionDate).toFormat("yyyy-MM-dd");
            const fileName = `${prefix}X${datePart}X${this.measurementType}.xlsx`;
            const renamedFile = new File([file], fileName, { type: file.type });
            return renamedFile;
        },
        getFilenamePrefix(plateName) {
            if (this.codeType === "experiment") {
                const experimentCode = this.code.startsWith("EXP")
                    ? this.code.substring(3)
                    : this.code;
                const plateNumber = plateName.startsWith("PLT")
                    ? plateName.substring(3)
                    : plateName;
                return `${experimentCode}_${plateNumber}`;
            } else {
                // request
                const requestCode = this.code;
                // plate_name looks like "REQ1639_plate_2" and we want only the trailing number
                const plateNumber = plateName.split("_").pop();
                return `${requestCode}_${plateNumber}`;
            }
        },
        mappingChanged(plateMap) {
            this.plateMap = plateMap;
        },
        buildGroupsToPlatesMap() {
            // this.plates looks like:
            // {
            //     name: string;
            //     plateGroup: string;
            // }
            const groupsToPlatesMap = {};
            this.plates.forEach((plate) => {
                if (!groupsToPlatesMap[plate.plateGroup]) {
                    groupsToPlatesMap[plate.plateGroup] = [];
                }
                groupsToPlatesMap[plate.plateGroup].push(plate.name);
            });
            this.groupsToPlatesMap = groupsToPlatesMap;
        },
    },
});
</script>
